import { createDeck, shuffleDeck, pokerCardsOnly } from './cards.js';
import { evaluate7, compareHands } from './handEval.js';
import { buildSidePots, splitPot } from './pots.js';
import { decideCpuAction } from './aiDecide.js';

export const NUM_SEATS = 6;
export const HERO_SEAT = 0;
export const START_STACK = 1500;
export const SMALL_BLIND = 25;
export const BIG_BLIND = 50;
export const TABLE_ID = 184;
export const ACTION_TIMER_SEC = 12;

/** @typedef {import('./cards.js').Card} Card */

/**
 * @typedef {object} PlayerState
 * @property {number} seat
 * @property {number} stack
 * @property {[Card, Card]|null} hole
 * @property {boolean} folded
 * @property {number} betThisRound
 * @property {number} totalBet
 * @property {boolean} actedThisRound
 */

/**
 * @typedef {object} PendingBlindAnim
 * @property {number} handSeq
 * @property {number} sbSeat
 * @property {number} bbSeat
 * @property {number} sbAmount
 * @property {number} bbAmount
 */

/**
 * @typedef {object} TableState
 * @property {'between'|'betting'|'showdown'} phase
 * @property {'preflop'|'flop'|'turn'|'river'} street
 * @property {Card[]} deck
 * @property {Card[]} community
 * @property {PlayerState[]} players
 * @property {number} dealerSeat
 * @property {number|null} activeSeat
 * @property {number} lastRaiseIncrement
 * @property {number} handSeq
 * @property {Record<number, { displayName: string, archetype: string, personality: object }>} opponentRoster
 * @property {'easy'|'medium'|'hard'} aiDifficulty
 * @property {PendingBlindAnim|null} pendingBlindAnim
 * @property {{ sb: number, bb: number }|null} handBlindSeats
 */

function freshPlayers() {
  return Array.from({ length: NUM_SEATS }, (_, seat) => ({
    seat,
    stack: START_STACK,
    hole: null,
    folded: false,
    betThisRound: 0,
    totalBet: 0,
    actedThisRound: false,
  }));
}

/** @returns {TableState} */
export function createInitialTableState() {
  return {
    phase: 'between',
    street: 'preflop',
    deck: [],
    community: [],
    players: freshPlayers(),
    dealerSeat: -1,
    activeSeat: null,
    lastRaiseIncrement: BIG_BLIND,
    handSeq: 0,
    opponentRoster: {},
    aiDifficulty: 'medium',
    pendingBlindAnim: null,
    handBlindSeats: null,
  };
}

function seatMod(i) {
  return ((i % NUM_SEATS) + NUM_SEATS) % NUM_SEATS;
}

export function smallBlindSeat(dealer) {
  return seatMod(dealer + 1);
}

export function bigBlindSeat(dealer) {
  return seatMod(dealer + 2);
}

function maxBetRound(players) {
  const active = players.filter((p) => !p.folded);
  if (active.length === 0) return 0;
  return Math.max(...active.map((p) => p.betThisRound));
}

function nonFoldedCount(players) {
  return players.filter((p) => !p.folded).length;
}

function bettingRoundComplete(players) {
  const active = players.filter((p) => !p.folded);
  if (active.length <= 1) return true;
  const mb = maxBetRound(players);
  const unmatched = active.filter((p) => p.stack > 0 && p.betThisRound < mb);
  if (unmatched.length > 0) return false;
  const canRaise = active.filter((p) => p.stack > 0);
  if (canRaise.length === 0) return true;
  return canRaise.every((p) => p.actedThisRound);
}

function clearRoundBets(players) {
  players.forEach((p) => {
    p.betThisRound = 0;
    p.actedThisRound = false;
  });
}

function nextActiveSeatFrom(players, fromSeat) {
  for (let k = 1; k <= NUM_SEATS; k += 1) {
    const s = seatMod(fromSeat + k);
    const p = players[s];
    if (p.folded || p.stack <= 0) continue;
    const mb = maxBetRound(players);
    if (p.betThisRound < mb) return s;
    if (!p.actedThisRound) return s;
  }
  return null;
}

function postBlind(players, seat, amount) {
  const p = players[seat];
  const pay = Math.min(p.stack, amount);
  p.stack -= pay;
  p.betThisRound += pay;
  p.totalBet += pay;
}

/**
 * @param {TableState} state
 * @param {number} rng
 */
export function startNewHand(state, rng = Math.random) {
  state.handSeq += 1;
  state.phase = 'betting';
  state.street = 'preflop';
  state.community = [];
  state.deck = shuffleDeck(createDeck(), rng);
  state.players.forEach((p) => {
    p.hole = null;
    p.folded = false;
    p.betThisRound = 0;
    p.totalBet = 0;
    p.actedThisRound = false;
  });

  const alive = state.players.filter((p) => p.stack > 0);
  if (alive.length < 2) {
    state.phase = 'between';
    state.activeSeat = null;
    state.pendingBlindAnim = null;
    state.handBlindSeats = null;
    return { heroBusted: !state.players[HERO_SEAT].stack };
  }

  state.dealerSeat = seatMod(state.dealerSeat + 1);
  while (state.players[state.dealerSeat].stack <= 0) {
    state.dealerSeat = seatMod(state.dealerSeat + 1);
  }

  const beforeStack = state.players.map((p) => p.stack);
  const sbS = nextOccupiedSeat(state.players, smallBlindSeat(state.dealerSeat));
  const bbS = nextOccupiedSeat(state.players, seatMod(sbS + 1));
  const sbPay = Math.min(beforeStack[sbS], SMALL_BLIND);
  const bbPay = Math.min(beforeStack[bbS], BIG_BLIND);
  postBlind(state.players, sbS, SMALL_BLIND);
  postBlind(state.players, bbS, BIG_BLIND);
  state.handBlindSeats = { sb: sbS, bb: bbS };
  state.pendingBlindAnim = {
    handSeq: state.handSeq,
    sbSeat: sbS,
    bbSeat: bbS,
    sbAmount: sbPay,
    bbAmount: bbPay,
  };

  const dealOrder = [];
  for (let k = 0; k < NUM_SEATS; k += 1) {
    const s = seatMod(sbS + k);
    if (beforeStack[s] > 0) dealOrder.push(s);
  }

  let cardIdx = 0;
  for (let r = 0; r < 2; r += 1) {
    for (const s of dealOrder) {
      const c = state.deck[cardIdx];
      cardIdx += 1;
      const pl = state.players[s];
      if (!pl.hole) pl.hole = [c, null];
      else pl.hole[1] = c;
    }
  }
  state.deck = state.deck.slice(cardIdx);

  state.lastRaiseIncrement = BIG_BLIND;
  state.activeSeat = nextOccupiedSeat(state.players, seatMod(bbS + 1));
  markActedIfSingleOption(state);

  return { heroBusted: false };
}

function nextOccupiedSeat(players, start) {
  for (let k = 0; k < NUM_SEATS; k += 1) {
    const s = seatMod(start + k);
    if (players[s].stack > 0 || players[s].totalBet > 0) return s;
  }
  return start;
}

function markActedIfSingleOption(state) {
  const p = state.activeSeat != null ? state.players[state.activeSeat] : null;
  if (!p) return;
  const mb = maxBetRound(state.players);
  if (p.stack > 0 && p.betThisRound >= mb && mb === 0) {
    p.actedThisRound = true;
  }
}

function advanceStreet(state) {
  clearRoundBets(state.players);
  state.lastRaiseIncrement = BIG_BLIND;

  if (state.street === 'preflop') {
    state.street = 'flop';
    for (let i = 0; i < 3; i += 1) {
      const c = state.deck.shift();
      if (c) state.community.push(c);
    }
  } else if (state.street === 'flop') {
    state.street = 'turn';
    const c = state.deck.shift();
    if (c) state.community.push(c);
  } else if (state.street === 'turn') {
    state.street = 'river';
    const c = state.deck.shift();
    if (c) state.community.push(c);
  } else {
    return false;
  }

  const first = nextSeatAfterDealer(state.players, state.dealerSeat);
  state.activeSeat = firstPlayerToActNewStreet(state.players, first);
  return true;
}

/** All community cards and showdown when every non-folded player is all-in */
function runOutAndShowdown(state) {
  while (state.street !== 'river') {
    if (!advanceStreet(state)) break;
  }
  state.phase = 'showdown';
  state.activeSeat = null;
  resolveAndCloseHand(state);
}

function nextSeatAfterDealer(players, dealer) {
  for (let k = 1; k <= NUM_SEATS; k += 1) {
    const s = seatMod(dealer + k);
    if (!players[s].folded && (players[s].stack > 0 || players[s].totalBet > 0)) return s;
  }
  return seatMod(dealer + 1);
}

function firstPlayerToActNewStreet(players, start) {
  for (let k = 0; k < NUM_SEATS; k += 1) {
    const s = seatMod(start + k);
    if (!players[s].folded && players[s].stack > 0) return s;
  }
  return null;
}

/**
 * @returns {{ heroWinnings: number, winners: Map<number, number> } | null}
 */
export function resolveShowdown(state) {
  const rows = state.players.map((p) => ({
    seat: p.seat,
    totalBet: p.totalBet,
    folded: p.folded,
  }));
  const pots = buildSidePots(rows);
  const winnings = new Map();
  state.players.forEach((p) => winnings.set(p.seat, 0));

  for (const pot of pots) {
    const contenders = pot.eligibleSeats
      .map((s) => state.players[s])
      .filter((p) => p && !p.folded);
    if (contenders.length === 0) continue;
    const board = state.community;
    let best = null;
    for (const p of contenders) {
      const cards = pokerCardsOnly([...(p.hole || []), ...board]);
      const ev = evaluate7(cards);
      const cmp = best == null ? 1 : compareHands(ev, best.hand);
      if (cmp > 0) {
        best = { hand: ev, seats: [p.seat] };
      } else if (cmp === 0) {
        best.seats.push(p.seat);
      }
    }
    const split = splitPot(pot.amount, best.seats);
    split.forEach((amt, seat) => {
      winnings.set(seat, (winnings.get(seat) || 0) + amt);
    });
  }

  state.players.forEach((p) => {
    p.stack += winnings.get(p.seat) || 0;
  });

  const heroWinnings = winnings.get(HERO_SEAT) || 0;
  return { heroWinnings, winners: winnings };
}

function awardUncontested(state) {
  const alive = state.players.filter((p) => !p.folded);
  if (alive.length !== 1) return null;
  const w = alive[0];
  const pot = state.players.reduce((s, p) => s + p.totalBet, 0);
  w.stack += pot;
  const heroWinnings = w.seat === HERO_SEAT ? pot : 0;
  return { heroWinnings, winners: new Map([[w.seat, pot]]) };
}

export function fold(state, seat) {
  const p = state.players[seat];
  if (state.phase !== 'betting' || state.activeSeat !== seat) return;
  p.folded = true;
  p.actedThisRound = true;
  afterAction(state);
}

export function check(state, seat) {
  const p = state.players[seat];
  if (state.phase !== 'betting' || state.activeSeat !== seat) return;
  const mb = maxBetRound(state.players);
  if (p.betThisRound < mb) return;
  p.actedThisRound = true;
  afterAction(state);
}

export function call(state, seat) {
  const p = state.players[seat];
  if (state.phase !== 'betting' || state.activeSeat !== seat) return;
  const mb = maxBetRound(state.players);
  const need = mb - p.betThisRound;
  const pay = Math.min(p.stack, need);
  p.stack -= pay;
  p.betThisRound += pay;
  p.totalBet += pay;
  p.actedThisRound = true;
  afterAction(state);
}

/**
 * Raise by adding `extra` chips beyond the call amount (NL Hold'em).
 * @param {TableState} state
 * @param {number} seat
 * @param {number} extra minimum lastRaiseIncrement (or all-in for less)
 */
export function raiseBy(state, seat, extra) {
  const p = state.players[seat];
  if (state.phase !== 'betting' || state.activeSeat !== seat) return;
  const mb = maxBetRound(state.players);
  const toCall = mb - p.betThisRound;
  const minExtra = Math.min(p.stack - toCall, state.lastRaiseIncrement);
  let addExtra = Math.max(extra, minExtra > 0 ? minExtra : 0);
  if (addExtra <= 0 && p.stack > toCall) addExtra = Math.min(state.lastRaiseIncrement, p.stack - toCall);
  let totalPay = Math.min(p.stack, toCall + addExtra);
  if (totalPay <= 0) return;
  p.stack -= totalPay;
  p.betThisRound += totalPay;
  p.totalBet += totalPay;
  const raisedBy = p.betThisRound - mb;
  if (raisedBy > 0) {
    state.lastRaiseIncrement = Math.max(state.lastRaiseIncrement, raisedBy);
    state.players.forEach((pl) => {
      if (pl.seat !== seat && pl.stack > 0) pl.actedThisRound = false;
    });
  }
  p.actedThisRound = true;
  afterAction(state);
}

export function raiseToTotal(state, seat, targetRoundTotal) {
  const p = state.players[seat];
  if (state.phase !== 'betting' || state.activeSeat !== seat) return;
  const mb = maxBetRound(state.players);
  const want = Math.max(targetRoundTotal, mb + state.lastRaiseIncrement);
  const extra = want - p.betThisRound;
  const pay = Math.min(p.stack, extra);
  if (pay <= 0) return;
  const prevMb = mb;
  p.stack -= pay;
  p.betThisRound += pay;
  p.totalBet += pay;
  if (p.betThisRound > prevMb) {
    const raisedBy = p.betThisRound - prevMb;
    state.lastRaiseIncrement = Math.max(state.lastRaiseIncrement, raisedBy);
    state.players.forEach((pl) => {
      if (pl.seat !== seat && pl.stack > 0) pl.actedThisRound = false;
    });
  }
  p.actedThisRound = true;
  afterAction(state);
}

function afterAction(state) {
  if (nonFoldedCount(state.players) <= 1) {
    endHandUncontested(state);
    return;
  }
  if (bettingRoundComplete(state.players)) {
    const active = state.players.filter((p) => !p.folded);
    const allInMulti = active.length > 1 && active.every((p) => p.stack === 0);
    if (allInMulti) {
      runOutAndShowdown(state);
      return;
    }
    if (state.street === 'river') {
      state.phase = 'showdown';
      state.activeSeat = null;
      resolveAndCloseHand(state);
      return;
    }
    const ok = advanceStreet(state);
    if (!ok) {
      state.phase = 'showdown';
      resolveAndCloseHand(state);
      return;
    }
    if (state.activeSeat == null) {
      runOutAndShowdown(state);
    }
    return;
  }
  const next = nextActiveSeatFrom(state.players, state.activeSeat);
  state.activeSeat = next;
  if (state.activeSeat == null && state.phase === 'betting') {
    if (bettingRoundComplete(state.players)) {
      afterAction(state);
      return;
    }
    const active = state.players.filter((p) => !p.folded);
    if (active.length > 1 && active.every((p) => p.stack === 0)) {
      runOutAndShowdown(state);
    }
  }
}

function endHandUncontested(state) {
  const r = awardUncontested(state);
  state.phase = 'between';
  state.activeSeat = null;
  state._lastHeroWinnings = r ? r.heroWinnings : 0;
}

function resolveAndCloseHand(state) {
  const r = resolveShowdown(state);
  state.phase = 'between';
  state.activeSeat = null;
  state._lastHeroWinnings = r ? r.heroWinnings : 0;
}

/** Call after phase is between to prepare next hand */
export function collectHandResult(state) {
  const w = state._lastHeroWinnings;
  state._lastHeroWinnings = 0;
  return typeof w === 'number' ? w : 0;
}

/**
 * Run CPU actions until hero's turn or hand ends.
 * @returns {'hero'|'between'}
 */
export function runCpuActions(state) {
  if (state.phase === 'betting' && state.activeSeat == null) {
    if (bettingRoundComplete(state.players)) {
      afterAction(state);
    } else {
      const active = state.players.filter((p) => !p.folded);
      if (active.length > 1 && active.every((p) => p.stack === 0)) {
        runOutAndShowdown(state);
      }
    }
  }
  let guard = 0;
  while (state.phase === 'betting' && state.activeSeat != null && state.activeSeat !== HERO_SEAT && guard < 200) {
    guard += 1;
    cpuDecide(state, state.activeSeat);
  }
  if (state.phase === 'between') return 'between';
  if (state.activeSeat === HERO_SEAT) return 'hero';
  return 'between';
}

function cpuDecide(state, seat) {
  const beforeSeat = state.activeSeat;
  const d = decideCpuAction(state, seat, Math.random);
  if (d.action === 'fold') fold(state, seat);
  else if (d.action === 'check') check(state, seat);
  else if (d.action === 'call') call(state, seat);
  else {
    raiseBy(state, seat, Math.max(0, d.raiseExtra));
    if (state.phase === 'betting' && state.activeSeat === beforeSeat) {
      const p = state.players[seat];
      const mb = maxBetRound(state.players);
      if (p.betThisRound < mb) call(state, seat);
      else check(state, seat);
    }
  }
}

export function heroToCall(state) {
  const p = state.players[HERO_SEAT];
  if (p.folded) return 0;
  const mb = maxBetRound(state.players);
  return Math.min(p.stack, mb - p.betThisRound);
}

export function heroCanCheck(state) {
  const p = state.players[HERO_SEAT];
  const mb = maxBetRound(state.players);
  return p.betThisRound >= mb;
}

export function heroMinRaiseExtra(state) {
  const p = state.players[HERO_SEAT];
  const mb = maxBetRound(state.players);
  const toCall = mb - p.betThisRound;
  const minTotal = mb + state.lastRaiseIncrement;
  const need = minTotal - p.betThisRound;
  return Math.max(0, Math.min(p.stack - toCall, need - toCall));
}

export function potTotal(state) {
  return state.players.reduce((s, p) => s + p.totalBet, 0);
}

export function streetLabelHe(street) {
  const m = { preflop: 'פרה-פלופ', flop: 'פלופ', turn: 'טרן', river: 'ריבר' };
  return m[street] || street;
}
